import { requestsSummaryActivity } from "@/lib/covi/summaryActivityPreference"

import {
  FOCUS_SECTION_TITLE,
  MAX_SIGNAL_ITEMS,
  MS_PER_DAY,
  TIMEFRAME_DAYS,
  WHAT_CHANGED_SECTION_TITLE,
  WORTH_DIGGING_SECTION_TITLE,
} from "./conversationsSummaryConstants"
import {
  buildFocusItems,
  buildSummaryText,
  buildWhatChangedItems,
  buildWorthDiggingItems,
  getLeadSentence,
  getMetricIssueCounts,
  getNumericMetricMovements,
  getOutcomeMetricMovements,
  getStringMetricSignals,
  parseConversationsSummaryFocusText,
} from "./conversationsSummaryDetail"
import {
  formatCount,
  getTimeframeLabel,
  pluralize,
} from "./conversationsSummaryFormat"
import type {
  ConversationsPageSummary,
  ConversationsPageSummaryOptions,
  ConversationsSummaryTimeframe,
} from "./conversationsSummaryTypes"
import {
  calculateOutcomeWindowStats,
  calculateWindowStats,
  filterRowsForWindow,
  getDatedRows,
  getTimeframeForWindowDays,
  getWindowBounds,
  resolveTimeframe,
} from "./conversationsSummaryWindow"

function getUnsuccessfulConversationsSignal(count: number): string {
  return `${formatCount(count)} unsuccessful conversations with known status`
}

export function buildConversationsPageSummary(
  selectedTimeframe: ConversationsSummaryTimeframe,
  referenceDate: Date,
  options: ConversationsPageSummaryOptions = {},
): ConversationsPageSummary {
  const includeActivity = requestsSummaryActivity(options.focusText)
  const rows = options.rows ?? []
  const focus = parseConversationsSummaryFocusText(options.focusText)
  const datedRows = getDatedRows(rows)
  const resolvedTimeframe =
    selectedTimeframe === "auto" && options.outcomeInsights
      ? getTimeframeForWindowDays(options.outcomeInsights.windowDays)
      : resolveTimeframe(selectedTimeframe, datedRows, referenceDate)
  const windowDays = TIMEFRAME_DAYS[resolvedTimeframe]
  const currentBounds = getWindowBounds(referenceDate, windowDays)
  const previousBounds = {
    endTimeMs: currentBounds.startTimeMs,
    startTimeMs: currentBounds.startTimeMs - windowDays * MS_PER_DAY,
  }
  const currentRows = filterRowsForWindow(
    datedRows,
    currentBounds.startTimeMs,
    currentBounds.endTimeMs,
  )
  const previousRows = filterRowsForWindow(
    datedRows,
    previousBounds.startTimeMs,
    previousBounds.endTimeMs,
  )
  const exactInsights =
    options.outcomeInsights?.windowDays === windowDays
      ? options.outcomeInsights
      : undefined
  const currentStats = exactInsights
    ? calculateOutcomeWindowStats(exactInsights.current)
    : calculateWindowStats(currentRows)
  const previousStats = exactInsights
    ? calculateOutcomeWindowStats(exactInsights.previous)
    : calculateWindowStats(previousRows)
  const metricMovements = exactInsights
    ? getOutcomeMetricMovements(exactInsights.metrics, options.lookups)
    : getNumericMetricMovements(currentRows, previousRows, options.lookups)
  const metricIssues = getMetricIssueCounts(currentRows, options.lookups)
  const stringMetricSignals = getStringMetricSignals(
    currentRows,
    options.lookups,
  )
  const leadSentence = getLeadSentence({
    currentRows,
    exactMetrics: exactInsights?.metrics ?? [],
    focus,
    metricIssues,
    metricMovements,
    stringMetricSignals,
    lookups: options.lookups,
  })
  const timeframeLabel = getTimeframeLabel(resolvedTimeframe)
  const summaryText = buildSummaryText({
    currentStats,
    leadSentence,
    previousStats,
    resolvedTimeframe,
    timeframeLabel,
    includeActivity,
  })
  const detailSections = [
    {
      title: FOCUS_SECTION_TITLE,
      items: buildFocusItems({
        currentRows,
        focus,
        metricIssues,
        metricMovements,
        stringMetricSignals,
        lookups: options.lookups,
      }),
    },
    {
      title: WHAT_CHANGED_SECTION_TITLE,
      items: buildWhatChangedItems({
        currentRows,
        focus,
        metricMovements,
        previousRows,
        lookups: options.lookups,
      }),
    },
    {
      title: WORTH_DIGGING_SECTION_TITLE,
      items: buildWorthDiggingItems({
        currentRows,
        focus,
        metricIssues,
        stringMetricSignals,
        lookups: options.lookups,
      }),
    },
  ].filter((section) => section.items.length > 0)

  const signalItems = [
    includeActivity && currentStats.unsuccessfulConversations > 0
      ? getUnsuccessfulConversationsSignal(
          currentStats.unsuccessfulConversations,
        )
      : null,
    metricIssues.length > 0
      ? `${formatCount(metricIssues.length)} metric issue ${pluralize(
          metricIssues.length,
          "cluster",
        )}`
      : null,
    ...detailSections.flatMap((section) => section.items),
  ]
    .filter((item): item is string => item !== null)
    .slice(0, MAX_SIGNAL_ITEMS)

  return {
    selectedTimeframe,
    resolvedTimeframe,
    windowDays,
    timeframeLabel:
      selectedTimeframe === "auto" ? `Auto: ${timeframeLabel}` : timeframeLabel,
    summaryText,
    focus,
    signalItems,
    detailSections,
    detailRowCount: currentRows.length,
    currentStats,
  }
}

export function getConversationsPageSummaryInsightSections(
  summary: ConversationsPageSummary,
): { changed: string[]; concerns: string[] } {
  const detailConcerns =
    summary.detailSections.find(
      (section) => section.title === WORTH_DIGGING_SECTION_TITLE,
    )?.items ?? []
  const exactFailureConcern =
    requestsSummaryActivity(summary.focus.rawText) &&
    summary.currentStats.unsuccessfulConversations > 0
      ? getUnsuccessfulConversationsSignal(
          summary.currentStats.unsuccessfulConversations,
        )
      : null

  return {
    changed:
      summary.detailSections.find(
        (section) => section.title === WHAT_CHANGED_SECTION_TITLE,
      )?.items ?? [],
    concerns: exactFailureConcern
      ? [exactFailureConcern, ...detailConcerns]
      : detailConcerns,
  }
}
